import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, XCircle, Loader2, ArrowRight, Home, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentResult() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  
  const orderId = searchParams.get('order_id');
  const isSuccess = window.location.pathname.includes('success');
  const isCancelled = window.location.pathname.includes('cancelled');

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    } else {
      setLoading(false);
    }
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/building-payments/order/${orderId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        setOrderDetails(await response.json());
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-8 pb-6 text-center">
          {isSuccess ? (
            <>
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h1>
              <p className="text-slate-600 mb-6">
                {orderDetails ? (
                  <>
                    Your payment of <span className="font-semibold">R{orderDetails.total_amount?.toFixed(2)}</span> has been processed.
                    {orderDetails.items?.length > 0 && (
                      <span> {orderDetails.items.length} building(s) have been activated.</span>
                    )}
                  </>
                ) : (
                  'Your payment has been processed successfully. Your buildings will be activated shortly.'
                )}
              </p>
              
              {orderDetails?.items?.length > 0 && (
                <div className="bg-emerald-50 rounded-lg p-4 mb-6 text-left">
                  <h3 className="font-medium text-emerald-800 mb-2 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Activated Buildings
                  </h3>
                  <ul className="space-y-1">
                    {orderDetails.items.map((item, idx) => (
                      <li key={idx} className="text-sm text-emerald-700">
                        {item.building_name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-3">
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => navigate('/agent')}
                >
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/agent/building-payments')}
                >
                  View All Payments
                </Button>
              </div>
            </>
          ) : isCancelled ? (
            <>
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Cancelled</h1>
              <p className="text-slate-600 mb-6">
                Your payment was cancelled. No charges have been made. You can try again or request an invoice instead.
              </p>

              <div className="space-y-3">
                <Button 
                  className="w-full"
                  onClick={() => navigate('/agent/building-payments')}
                >
                  Return to Payments
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/agent')}
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go to Dashboard
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-12 h-12 text-slate-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment Status</h1>
              <p className="text-slate-600 mb-6">
                Unable to determine payment status. Please check your payments dashboard for details.
              </p>

              <Button 
                className="w-full"
                onClick={() => navigate('/agent/building-payments')}
              >
                View Payments
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
